import React, { useState, useEffect } from 'react';
import {
  Card, Col, Row, Table, message, Tag, Statistic, Empty, Spin, Tabs, Select,
  Descriptions, Space, List, Button, Modal, Form, Input, Alert, Upload, DatePicker,
  Avatar, Tooltip, Popconfirm,
} from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  TrophyOutlined, FileTextOutlined, CalendarOutlined, SafetyOutlined,
  CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, PhoneOutlined,
  MailOutlined, UserOutlined, TeamOutlined, IdcardOutlined, EnvironmentOutlined,
  LineChartOutlined, DashboardOutlined, AlertOutlined, PlusOutlined, EditOutlined,
  SaveOutlined, CopyOutlined, UploadOutlined, DeleteOutlined, EyeOutlined,
  ContactsOutlined,
} from '@ant-design/icons';

const { Option }    = Select;
const { TabPane }   = Tabs;
const { TextArea }  = Input;

// ── Sabitler ──────────────────────────────────────────────────────────────────
const CERTIFICATE_OPTIONS = [
  { label: 'IATF 16949', value: 'IATF 16949' },
  { label: 'ISO 9001',   value: 'ISO 9001'   },
  { label: 'ISO 14001',  value: 'ISO 14001'  },
];

const SITUATION_OPTIONS = [
  { value: '1', label: 'Approved', color: 'green'  },
  { value: '0', label: 'Pending',  color: 'orange' },
  { value: '2', label: 'Rejected', color: 'red'    },
];

const CONTACT_TYPE_COLORS = {
  Manager:   '#1890ff',
  Technical: '#52c41a',
  Sales:     '#fa8c16',
  Support:   '#722ed1',
  Finance:   '#eb2f96',
  Quality:   '#13c2c2',
  Logistics: '#faad14',
  Default:   '#8c8c8c',
};

const getContactTypeColor = (type) =>
  CONTACT_TYPE_COLORS[type] || CONTACT_TYPE_COLORS.Default;

// ─────────────────────────────────────────────────────────────────────────────

const SupplierPage = () => {
  const { t } = useTranslation();

  // ── Genel state ───────────────────────────────────────────────────────────
  const [suppliers,          setSuppliers]          = useState([]);
  const [selectedSupplier,   setSelectedSupplier]   = useState(null);
  const [supplierDetails,    setSupplierDetails]    = useState(null);
  const [certificates,       setCertificates]       = useState([]);
  const [contacts,           setContacts]           = useState([]);
  const [contactTypes,       setContactTypes]       = useState([]);
  const [auditDates,         setAuditDates]         = useState([]);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [latestPerformance,  setLatestPerformance]  = useState(null);
  const [loading,            setLoading]            = useState(false);
  const [activeTab,          setActiveTab]          = useState('overview');

  // ── Supplier modal ────────────────────────────────────────────────────────
  const [addModalVisible,  setAddModalVisible]  = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [submitting,       setSubmitting]       = useState(false);
  const [addForm]  = Form.useForm();
  const [editForm] = Form.useForm();

  // ── Certificate modal ─────────────────────────────────────────────────────
  const [certAddModalVisible,  setCertAddModalVisible]  = useState(false);
  const [certEditModalVisible, setCertEditModalVisible] = useState(false);
  const [certSubmitting,       setCertSubmitting]       = useState(false);
  const [editingCert,          setEditingCert]          = useState(null);
  const [certAddFileList,      setCertAddFileList]      = useState([]);
  const [certEditFileList,     setCertEditFileList]     = useState([]);
  const [certAddForm]  = Form.useForm();
  const [certEditForm] = Form.useForm();

  // ── Contact modal ─────────────────────────────────────────────────────────
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [contactSubmitting,   setContactSubmitting]   = useState(false);
  const [editingContact,      setEditingContact]      = useState(null); // null → add, obj → edit
  const [contactForm] = Form.useForm();

  const apiUrl    = process.env.REACT_APP_API_URL;
  const token     = localStorage.getItem('urlToken');
  const authHeader = { Authorization: `Bearer ${token}` };

  // ── Suppliers list ────────────────────────────────────────────────────────
  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res  = await axios.get(`${apiUrl}/api/Suppliers/suppliers-only`, { headers: authHeader });
      const data = Array.isArray(res.data) ? res.data
        : res.data?.suppliers ?? res.data?.data ?? [];
      setSuppliers(data);
      if (data.length === 0) message.warning(t('no_suppliers_available'));
    } catch {
      message.error(t('data_fetch_error'));
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Contact types (bir kez yükle) ─────────────────────────────────────────
  const fetchContactTypes = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/SupplierContact/types`, { headers: authHeader });
      setContactTypes(res.data?.types ?? []);
    } catch {
      setContactTypes([]);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchContactTypes();
  }, []);

  // ── Certificates ──────────────────────────────────────────────────────────
  const fetchCertificates = async (supplierId) => {
    try {
      const res = await axios.get(`${apiUrl}/api/Certificates/supplier/${supplierId}`, { headers: authHeader });
      setCertificates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCertificates([]);
    }
  };

  // ── Contacts ──────────────────────────────────────────────────────────────
  const fetchContacts = async (supplierId) => {
    try {
      const res = await axios.get(`${apiUrl}/api/SupplierContact/supplier/${supplierId}`, { headers: authHeader });
      const cd  = res.data?.contacts || res.data || [];
      setContacts(Array.isArray(cd) ? cd : []);
    } catch {
      setContacts([]);
    }
  };

  // ── Supplier change ───────────────────────────────────────────────────────
  const handleSupplierChange = async (supplierId) => {
    if (!supplierId) return;
    setSelectedSupplier(supplierId);
    setLoading(true);
    setActiveTab('overview');

    try {
      const detailsRes = await axios.get(`${apiUrl}/api/Suppliers/${supplierId}`, { headers: authHeader });
      setSupplierDetails(detailsRes.data || null);

      await fetchCertificates(supplierId);
      await fetchContacts(supplierId);

      try {
        const auditsRes = await axios.get(`${apiUrl}/api/SupplierAudit/supplier/${supplierId}`, { headers: authHeader });
        const ad = auditsRes.data?.audits || auditsRes.data || [];
        setAuditDates((Array.isArray(ad) ? ad : []).sort((a, b) => moment(a.auditDate) - moment(b.auditDate)));
      } catch { setAuditDates([]); }

      try {
        const perfRes = await axios.get(`${apiUrl}/api/SupplierEvaluations/${supplierId}/history`, { headers: authHeader });
        const pd = perfRes.data?.performanceHistory || perfRes.data || [];
        const ph = Array.isArray(pd) ? pd : [];
        setPerformanceHistory(ph);
        setLatestPerformance(ph[0] || null);
      } catch {
        setPerformanceHistory([]);
        setLatestPerformance(null);
      }
    } catch {
      message.error(t('data_fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // SUPPLIER CRUD
  // ══════════════════════════════════════════════════════════════════════════
  const handleAddSupplier = async (values) => {
    setSubmitting(true);
    try {
      const credRes = await axios.get(`${apiUrl}/api/Suppliers/generate-credentials`, { headers: authHeader });
      const { supplierCode, defaultPassword } = credRes.data;

      const res = await axios.post(
        `${apiUrl}/api/Suppliers`,
        { supplierName: values.supplierName, category: values.category || null,
          businessEmail: values.businessEmail, address: values.address || null,
          supplierCode, password: defaultPassword },
        { headers: authHeader }
      );

      Modal.success({
        title: (
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#52c41a' }}>
            <CheckCircleOutlined style={{ marginRight: '8px' }} />
            {t('supplier_created_successfully')}
          </div>
        ),
        content: (
          <div style={{ marginTop: '16px' }}>
            <div style={{ background: '#f0f5ff', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <p style={{ margin: '4px 0' }}>
                <strong>{t('supplier_code')}:</strong>
                <Tag color="blue" style={{ marginLeft: '8px', fontSize: '14px' }}>{supplierCode}</Tag>
              </p>
              <p style={{ margin: '4px 0' }}><strong>{t('email')}:</strong> {res.data.email}</p>
            </div>
            <div style={{ background: '#fff1f0', padding: '16px', borderRadius: '8px', border: '2px solid #ffccc7' }}>
              <p style={{ margin: '0 0 12px 0', color: '#ff4d4f', fontWeight: '600', fontSize: '15px' }}>
                <WarningOutlined style={{ marginRight: '8px' }} />{t('important_password_info')}
              </p>
              <div style={{ background: '#fff', padding: '12px', borderRadius: '6px', border: '1px solid #d9d9d9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <code style={{ fontSize: '16px', fontWeight: '600', color: '#1890ff', fontFamily: 'monospace' }}>{defaultPassword}</code>
                <Button type="primary" size="small" icon={<CopyOutlined />}
                  onClick={() => { navigator.clipboard.writeText(defaultPassword); message.success(t('password_copied')); }}>
                  {t('copy')}
                </Button>
              </div>
              <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#ff4d4f' }}>⚠️ {t('save_password_warning')}</p>
            </div>
          </div>
        ),
        width: 550, okText: t('ok'), okButtonProps: { size: 'large' },
      });

      await fetchSuppliers();
      setAddModalVisible(false);
      addForm.resetFields();
    } catch (error) {
      let msg = t('error_creating_supplier');
      if (error.response?.data?.message) msg = error.response.data.message;
      else if (error.response?.data?.errors) {
        const e = error.response.data.errors;
        msg = Array.isArray(e) ? e.join(', ') : Object.values(e).flat().join(', ');
      }
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSupplier = async (values) => {
    if (!supplierDetails) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${apiUrl}/api/Suppliers/${supplierDetails.supplierId}`,
        { supplierName: values.supplierName, category: values.category || null,
          businessEmail: supplierDetails.businessEmail, address: values.address || null },
        { headers: authHeader }
      );
      message.success(t('supplier_updated_successfully'));
      await handleSupplierChange(supplierDetails.supplierId);
      await fetchSuppliers();
      setEditModalVisible(false);
    } catch (error) {
      message.error(error.response?.data?.message || t('error_updating_supplier'));
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = () => {
    if (!supplierDetails) return;
    editForm.setFieldsValue({
      supplierName: supplierDetails.supplierName,
      category:     supplierDetails.category,
      address:      supplierDetails.address,
    });
    setEditModalVisible(true);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // CERTIFICATE CRUD
  // ══════════════════════════════════════════════════════════════════════════
  const handleCertAdd = async (values) => {
    if (!selectedSupplier) return;
    setCertSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('SupplierId',      selectedSupplier);
      fd.append('CertificateName', values.certificateName);
      fd.append('ValidityDate',    values.validityDate.format('YYYY-MM-DD'));
      if (certAddFileList.length > 0 && certAddFileList[0].originFileObj)
        fd.append('File', certAddFileList[0].originFileObj);

      await axios.post(`${apiUrl}/api/Certificates/admin-add`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      message.success(t('certificate_added_successfully'));
      await fetchCertificates(selectedSupplier);
      setCertAddModalVisible(false);
      certAddForm.resetFields();
      setCertAddFileList([]);
    } catch (error) {
      message.error(error.response?.data?.message || t('error_adding_certificate'));
    } finally {
      setCertSubmitting(false);
    }
  };

  const openCertEditModal = (cert) => {
    setEditingCert(cert);
    setCertEditFileList([]);
    certEditForm.setFieldsValue({
      certificateName: cert.certificateName,
      validityDate:    moment(cert.certificateValidityDate),
      situation:       cert.situation,
    });
    setCertEditModalVisible(true);
  };

  const handleCertEdit = async (values) => {
    if (!editingCert) return;
    setCertSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('CertificateName', values.certificateName);
      fd.append('ValidityDate',    values.validityDate.format('YYYY-MM-DD'));
      fd.append('Situation',       values.situation);
      if (certEditFileList.length > 0 && certEditFileList[0].originFileObj)
        fd.append('File', certEditFileList[0].originFileObj);

      await axios.put(`${apiUrl}/api/Certificates/${editingCert.certificateId}/admin-update`, fd, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      message.success(t('certificate_updated_successfully'));
      await fetchCertificates(selectedSupplier);
      setCertEditModalVisible(false);
      certEditForm.resetFields();
      setCertEditFileList([]);
      setEditingCert(null);
    } catch (error) {
      message.error(error.response?.data?.message || t('error_updating_certificate'));
    } finally {
      setCertSubmitting(false);
    }
  };

  const handleCertDelete = async (certId) => {
    try {
      await axios.delete(`${apiUrl}/api/Certificates/${certId}/withfile`, { headers: authHeader });
      message.success(t('certificate_deleted_successfully'));
      await fetchCertificates(selectedSupplier);
    } catch (error) {
      message.error(error.response?.data?.message || t('certificate_delete_error'));
    }
  };

  const handleViewCertificate = async (certId) => {
    try {
      const res = await axios.get(`${apiUrl}/api/Certificates/${certId}/sas`, { headers: authHeader });
      if (res.data?.sasUrl) window.open(res.data.sasUrl, '_blank');
      else message.error(t('sas_url_error'));
    } catch {
      message.error(t('certificate_view_error'));
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // CONTACT CRUD
  // ══════════════════════════════════════════════════════════════════════════
  const openContactAddModal = () => {
    setEditingContact(null);
    contactForm.resetFields();
    setContactModalVisible(true);
  };

  const openContactEditModal = (contact) => {
    setEditingContact(contact);
    contactForm.setFieldsValue({
      name:          contact.name,
      phone:         contact.phone,
      email:         contact.email,
      contactTypeId: contact.contactTypeId,
    });
    setContactModalVisible(true);
  };

  const handleContactSubmit = async (values) => {
    if (!selectedSupplier) return;
    setContactSubmitting(true);
    try {
      const payload = {
        supplierId:    selectedSupplier,
        contactTypeId: values.contactTypeId,
        name:          values.name,
        phone:         values.phone,
        email:         values.email,
      };

      if (editingContact) {
        // ── Güncelle ──
        await axios.put(
          `${apiUrl}/api/SupplierContact/${editingContact.contactId}`,
          payload,
          { headers: { ...authHeader, 'Content-Type': 'application/json' } }
        );
        message.success(t('contact_updated_successfully'));
      } else {
        // ── Yeni ekle ──
        await axios.post(
          `${apiUrl}/api/SupplierContact`,
          payload,
          { headers: { ...authHeader, 'Content-Type': 'application/json' } }
        );
        message.success(t('contact_added_successfully'));
      }

      await fetchContacts(selectedSupplier);
      setContactModalVisible(false);
      contactForm.resetFields();
      setEditingContact(null);
    } catch (error) {
      if (error.response?.status === 409)
        message.error(t('email_already_exists'));
      else
        message.error(error.response?.data?.message ||
          (editingContact ? t('failed_to_update_contact') : t('failed_to_add_contact')));
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleContactDelete = async (contactId) => {
    try {
      await axios.delete(`${apiUrl}/api/SupplierContact/${contactId}`, { headers: authHeader });
      message.success(t('contact_deleted_successfully'));
      await fetchContacts(selectedSupplier);
    } catch (error) {
      message.error(error.response?.data?.message || t('failed_to_delete_contact'));
    }
  };

  const handleContactModalClose = () => {
    setContactModalVisible(false);
    setEditingContact(null);
    contactForm.resetFields();
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getPerformanceColor = (score) =>
    score >= 80 ? '#52c41a' : score >= 60 ? '#faad14' : '#ff4d4f';

  const getCertificateStatus = (cert) => {
    if (cert.situation === '0') return { color: 'orange', text: t('pending'),       icon: <ClockCircleOutlined /> };
    if (cert.situation === '2') return { color: 'red',    text: t('rejected'),      icon: <WarningOutlined />    };
    const days = moment(cert.certificateValidityDate).diff(moment(), 'days');
    if (days < 0)   return { color: 'red',  text: t('expired'),       icon: <WarningOutlined />    };
    if (days <= 30) return { color: 'gold', text: t('expiring_soon'), icon: <AlertOutlined />      };
    return               { color: 'green', text: t('valid'),          icon: <CheckCircleOutlined /> };
  };

  const getAuditStatus = (auditDate) => {
    const today = moment();
    const audit = moment(auditDate);
    if (audit.isBefore(today))          return { color: 'red',    text: t('overdue')   };
    if (audit.diff(today, 'days') <= 7) return { color: 'orange', text: t('upcoming')  };
    return                                     { color: 'green',  text: t('scheduled') };
  };

  // ── Certificate columns ───────────────────────────────────────────────────
  const certificateColumns = [
    {
      title: t('certificate_name'), dataIndex: 'certificateName', key: 'certificateName',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: t('validity_date'), dataIndex: 'certificateValidityDate', key: 'certificateValidityDate',
      render: (date) => (
        <span><CalendarOutlined style={{ marginRight: 6, color: '#1890ff' }} />{moment(date).format('DD/MM/YYYY')}</span>
      ),
      sorter: (a, b) => moment(a.certificateValidityDate).unix() - moment(b.certificateValidityDate).unix(),
    },
    {
      title: t('days_remaining'), key: 'daysRemaining', align: 'center',
      render: (_, record) => {
        const days = moment(record.certificateValidityDate).diff(moment(), 'days');
        return days >= 0
          ? <Tag color={days <= 30 ? 'orange' : 'green'}>{days} {t('days')}</Tag>
          : <Tag color="red">{t('expired')}</Tag>;
      },
    },
    {
      title: t('status'), key: 'status', align: 'center',
      render: (_, record) => {
        const s = getCertificateStatus(record);
        return <Tag icon={s.icon} color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: t('actions'), key: 'actions', align: 'center', width: 140,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#1890ff' }}
            onClick={() => handleViewCertificate(record.certificateId)} title={t('view')} />
          <Button type="text" icon={<EditOutlined />} style={{ color: '#faad14' }}
            onClick={() => openCertEditModal(record)} title={t('edit')} />
          <Button type="text" danger icon={<DeleteOutlined />} title={t('delete')}
            onClick={() => Modal.confirm({
              title: t('delete_confirmation'), content: t('delete_certificate_warning'),
              okText: t('yes'), okButtonProps: { danger: true }, cancelText: t('no'),
              onOk: () => handleCertDelete(record.certificateId),
            })} />
        </Space>
      ),
    },
  ];

  // ── Contact columns ───────────────────────────────────────────────────────
  const contactColumns = [
    {
      title: t('contact_info'), key: 'contactInfo',
      render: (_, record) => (
        <Space size={12}>
          <Avatar size={36}
            style={{ backgroundColor: getContactTypeColor(record.contactTypeName), fontSize: '14px' }}>
            {record.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <strong style={{ fontSize: '13px', display: 'block' }}>{record.name}</strong>
            <Tag color={getContactTypeColor(record.contactTypeName)} style={{ fontSize: '11px', marginTop: 2 }}>
              {record.contactTypeName}
            </Tag>
          </div>
        </Space>
      ),
    },
    {
      title: t('phone'), dataIndex: 'phone', key: 'phone',
      render: (phone) => (
        <Space size={6}>
          <PhoneOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          <span style={{ fontSize: '13px' }}>{phone || '-'}</span>
        </Space>
      ),
    },
    {
      title: t('email'), dataIndex: 'email', key: 'email',
      render: (email) => (
        <Space size={6}>
          <MailOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
          <span style={{ fontSize: '13px', color: '#1890ff' }}>{email || '-'}</span>
        </Space>
      ),
    },
    {
      title: t('last_update'), dataIndex: 'lastUpdateDate', key: 'lastUpdateDate', width: 130,
      render: (date) => (
        <div>
          <div style={{ fontSize: '13px', color: '#595959' }}>{moment(date).format('DD/MM/YYYY')}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{moment(date).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      title: t('actions'), key: 'actions', align: 'center', width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('edit')}>
            <Button type="text" icon={<EditOutlined />} style={{ color: '#faad14' }}
              onClick={() => openContactEditModal(record)} />
          </Tooltip>
          <Tooltip title={t('delete')}>
            <Popconfirm
              title={t('delete_contact')}
              description={t('delete_confirmation')}
              onConfirm={() => handleContactDelete(record.contactId)}
              okText={t('yes')} cancelText={t('no')} okButtonProps={{ danger: true }}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <div style={{
        background: '#fff', padding: '32px', borderRadius: '12px',
        marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #1890ff',
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#262626', margin: 0 }}>
              <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
              {t('supplier_details')}
            </h1>
            <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: '15px' }}>
              {t('view_supplier_information')}
            </p>
          </Col>
          <Col>
            <Button type="primary" size="large" icon={<PlusOutlined />}
              onClick={() => setAddModalVisible(true)} style={{ fontWeight: '600' }}>
              {t('add_supplier')}
            </Button>
          </Col>
        </Row>
      </div>

      {/* ── Supplier Select ── */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
            <IdcardOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            {t('select_supplier')}
          </div>
          <Select showSearch style={{ width: '100%' }} placeholder={t('select_supplier_placeholder')}
            onChange={handleSupplierChange} loading={loading} size="large"
            optionFilterProp="children"
            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}>
            {suppliers.map((s) => (
              <Option key={s.supplierId} value={s.supplierId}>
                {s.supplierName} ({s.supplierCode})
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {loading && (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" tip={t('loading')} />
        </div>
      )}

      {!loading && selectedSupplier && supplierDetails && (
        <>
          {/* ── Stats ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {[
              { title: t('performance_score'), value: latestPerformance?.totalScore || 0,    suffix: '/ 100',         icon: <TrophyOutlined   style={{ color: '#faad14' }} />, color: getPerformanceColor(latestPerformance?.totalScore || 0) },
              { title: t('supplier_class'),    value: latestPerformance?.supplierClass || 'N/A',                      icon: <SafetyOutlined   style={{ color: '#1890ff' }} />, color: '#1890ff' },
              { title: t('total_certificates'),value: certificates.length,                                            icon: <FileTextOutlined style={{ color: '#52c41a' }} />, color: '#52c41a' },
              { title: t('total_contacts'),    value: contacts.length,                                                icon: <TeamOutlined     style={{ color: '#722ed1' }} />, color: '#722ed1' },
            ].map((stat, i) => (
              <Col key={i} xs={24} sm={12} lg={6}>
                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <Statistic title={stat.title} value={stat.value} suffix={stat.suffix}
                    prefix={stat.icon} valueStyle={{ color: stat.color, fontSize: '32px', fontWeight: '700' }} />
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── Tabs ── */}
          <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}
            bodyStyle={{ padding: 0 }}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} size="large"
              tabBarStyle={{ padding: '0 24px', margin: 0, background: '#fff', borderBottom: '1px solid #f0f0f0' }}>

              {/* ── Overview ── */}
              <TabPane tab={<span style={{ fontSize: '15px', fontWeight: '500' }}><IdcardOutlined style={{ marginRight: 8 }} />{t('overview')}</span>} key="overview">
                <div style={{ padding: '24px', background: '#fff' }}>
                  <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Button type="primary" icon={<EditOutlined />} onClick={openEditModal} size="large">
                      {t('edit_supplier')}
                    </Button>
                  </div>
                  <Descriptions
                    title={<span style={{ fontSize: '18px', fontWeight: '600' }}><SafetyOutlined style={{ marginRight: 8, color: '#1890ff' }} />{t('company_information')}</span>}
                    bordered column={{ xs: 1, sm: 2, md: 3 }} size="middle">
                    <Descriptions.Item label={t('supplier_name')}><strong>{supplierDetails.supplierName}</strong></Descriptions.Item>
                    <Descriptions.Item label={t('supplier_code')}><Tag color="blue">{supplierDetails.supplierCode}</Tag></Descriptions.Item>
                    <Descriptions.Item label={t('category')}><Tag color="green">{supplierDetails.category || '-'}</Tag></Descriptions.Item>
                    <Descriptions.Item label={t('level')}><Tag color="purple">{supplierDetails.supplierLevel || '-'}</Tag></Descriptions.Item>
                    <Descriptions.Item label={t('contact')}><PhoneOutlined style={{ marginRight: 8, color: '#52c41a' }} />{supplierDetails.businessContact || '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('email')}><MailOutlined style={{ marginRight: 8, color: '#faad14' }} />{supplierDetails.businessEmail}</Descriptions.Item>
                    <Descriptions.Item label={t('address')} span={3}><EnvironmentOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />{supplierDetails.address || '-'}</Descriptions.Item>
                  </Descriptions>

                  {latestPerformance && (
                    <Card title={<span style={{ fontSize: '18px', fontWeight: '600' }}><LineChartOutlined style={{ marginRight: 8, color: '#faad14' }} />{t('latest_performance')}</span>}
                      style={{ marginTop: '24px', borderRadius: '8px' }}>
                      <Row gutter={[16, 16]}>
                        {[
                          { title: t('total_score'),    value: latestPerformance.totalScore,               suffix: '/ 100', color: getPerformanceColor(latestPerformance.totalScore) },
                          { title: t('supplier_class'), value: latestPerformance.supplierClass,                              color: '#1890ff' },
                          { title: t('ppm'),            value: latestPerformance.ppm?.toFixed(2) || 'N/A',                  color: '#52c41a' },
                          { title: t('risk_level'),     value: latestPerformance.totalRiskLevel,                             color: latestPerformance.totalRiskLevel === 'Low' ? '#52c41a' : latestPerformance.totalRiskLevel === 'Medium' ? '#faad14' : '#ff4d4f' },
                        ].map((s, i) => (
                          <Col key={i} xs={24} sm={12} md={6}>
                            <Statistic title={s.title} value={s.value} suffix={s.suffix} valueStyle={{ color: s.color }} />
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  )}
                </div>
              </TabPane>

              {/* ── Certificates ── */}
              <TabPane
                tab={<span style={{ fontSize: '15px', fontWeight: '500' }}><FileTextOutlined style={{ marginRight: 8 }} />{t('certificates')} ({certificates.length})</span>}
                key="certificates">
                <div style={{ padding: '24px', background: '#fff' }}>
                  <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCertAddModalVisible(true)} size="large">
                      {t('add_certificate')}
                    </Button>
                  </div>
                  <Table dataSource={certificates} columns={certificateColumns} rowKey="certificateId"
                    pagination={{ pageSize: 10 }} locale={{ emptyText: <Empty description={t('no_certificates')} /> }} />
                </div>
              </TabPane>

              {/* ── Contacts ── */}
              <TabPane
                tab={<span style={{ fontSize: '15px', fontWeight: '500' }}><TeamOutlined style={{ marginRight: 8 }} />{t('contacts')} ({contacts.length})</span>}
                key="contacts">
                <div style={{ padding: '24px', background: '#fff' }}>
                  {/* ✅ Ekle butonu */}
                  <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openContactAddModal} size="large">
                      {t('add_new_contact')}
                    </Button>
                  </div>

                  <Table
                    dataSource={contacts}
                    columns={contactColumns}
                    rowKey="contactId"
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: <Empty description={t('no_contacts')} /> }}
                  />
                </div>
              </TabPane>

              {/* ── Audits ── */}
              <TabPane
                tab={<span style={{ fontSize: '15px', fontWeight: '500' }}><CalendarOutlined style={{ marginRight: 8 }} />{t('audits')} ({auditDates.length})</span>}
                key="audits">
                <div style={{ padding: '24px', background: '#fff' }}>
                  {auditDates.length > 0 ? (
                    <List itemLayout="horizontal" dataSource={auditDates}
                      renderItem={(audit) => {
                        const status = getAuditStatus(audit.auditDate);
                        const isPast = moment(audit.auditDate).isBefore(moment());
                        return (
                          <List.Item style={{ padding: '16px', marginBottom: '12px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                            <List.Item.Meta
                              avatar={
                                <div style={{ width: 48, height: 48, borderRadius: '8px', background: status.color === 'red' ? '#fff1f0' : status.color === 'orange' ? '#fff7e6' : '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${status.color === 'red' ? '#ffccc7' : status.color === 'orange' ? '#ffd591' : '#b7eb8f'}` }}>
                                  <CalendarOutlined style={{ fontSize: 20, color: status.color === 'red' ? '#ff4d4f' : status.color === 'orange' ? '#faad14' : '#52c41a' }} />
                                </div>
                              }
                              title={<Space><span style={{ fontSize: '16px', fontWeight: '600' }}>{moment(audit.auditDate).format('DD MMMM YYYY')}</span><Tag color={status.color}>{status.text}</Tag></Space>}
                              description={
                                <Space direction="vertical" size={4}>
                                  <span style={{ fontSize: '13px', color: '#8c8c8c' }}>
                                    {isPast ? <WarningOutlined style={{ marginRight: 4 }} /> : <ClockCircleOutlined style={{ marginRight: 4 }} />}
                                    {moment(audit.auditDate).fromNow()}
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#bfbfbf' }}>{moment(audit.auditDate).format('dddd')}</span>
                                </Space>
                              }
                            />
                          </List.Item>
                        );
                      }} />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no_audits')} />
                  )}
                </div>
              </TabPane>

              {/* ── Performance History ── */}
              <TabPane
                tab={<span style={{ fontSize: '15px', fontWeight: '500' }}><LineChartOutlined style={{ marginRight: 8 }} />{t('performance_history')}</span>}
                key="history">
                <div style={{ padding: '24px', background: '#fff' }}>
                  <Table dataSource={performanceHistory}
                    columns={[
                      { title: t('period'),         dataIndex: 'period',         key: 'period',         render: (v) => <Tag color="blue">{v}</Tag> },
                      { title: t('total_score'),    dataIndex: 'totalScore',     key: 'totalScore',     render: (s) => <span style={{ color: getPerformanceColor(s), fontWeight: '700', fontSize: '16px' }}>{s}</span>, sorter: (a, b) => a.totalScore - b.totalScore },
                      { title: t('supplier_class'), dataIndex: 'supplierClass',  key: 'supplierClass',  render: (c) => <Tag color="green">{c}</Tag> },
                      { title: t('ppm'),            dataIndex: 'ppm',            key: 'ppm',            render: (p) => p?.toFixed(2) || 'N/A' },
                      { title: t('risk_level'),     dataIndex: 'totalRiskLevel', key: 'totalRiskLevel', render: (l) => <Tag color={l === 'Low' ? 'green' : l === 'Medium' ? 'orange' : 'red'}>{l}</Tag> },
                    ]}
                    rowKey="performanceDataId" pagination={{ pageSize: 10 }}
                    locale={{ emptyText: <Empty description={t('no_history')} /> }} />
                </div>
              </TabPane>

            </Tabs>
          </Card>
        </>
      )}

      {!loading && !selectedSupplier && (
        <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '80px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span style={{ fontSize: '16px', color: '#8c8c8c' }}>{t('select_supplier_to_view')}</span>} />
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════════════════════ */}

      {/* ── Add Supplier ── */}
      <Modal
        title={<Space><PlusOutlined style={{ color: '#52c41a', fontSize: 20 }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('add_new_supplier')}</span></Space>}
        open={addModalVisible} onCancel={() => { setAddModalVisible(false); addForm.resetFields(); }} footer={null} width={600}>
        <Form form={addForm} layout="vertical" onFinish={handleAddSupplier} style={{ marginTop: 24 }}>
          <Form.Item name="supplierName" label={<strong>{t('supplier_name')}</strong>}
            rules={[{ required: true, message: t('please_enter_supplier_name') }, { min: 2, message: t('min_2_characters') }]}>
            <Input size="large" placeholder={t('enter_supplier_name')} prefix={<IdcardOutlined style={{ color: '#8c8c8c' }} />} />
          </Form.Item>
          <Form.Item name="category" label={<strong>{t('category')}</strong>}>
            <Input size="large" placeholder={t('enter_category_optional')} prefix={<SafetyOutlined style={{ color: '#8c8c8c' }} />} />
          </Form.Item>
          <Form.Item name="businessEmail" label={<strong>{t('email')}</strong>}
            rules={[{ required: true, message: t('please_enter_email') }, { type: 'email', message: t('invalid_email_format') }]}>
            <Input size="large" placeholder={t('enter_email')} prefix={<MailOutlined style={{ color: '#8c8c8c' }} />} />
          </Form.Item>
          <Form.Item name="address" label={<strong>{t('address')}</strong>}>
            <TextArea rows={3} placeholder={t('enter_address_optional')} />
          </Form.Item>
          <Alert message={t('auto_generated_info')}
            description={<ul style={{ margin: '8px 0', paddingLeft: 20 }}><li>{t('supplier_code_auto_generated')}</li><li>{t('strong_password_auto_generated')}</li><li>{t('role_automatically_supplier')}</li></ul>}
            type="info" showIcon style={{ marginBottom: 16 }} />
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setAddModalVisible(false); addForm.resetFields(); }} size="large">{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />} size="large">{t('create_supplier')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Supplier ── */}
      <Modal
        title={<Space><EditOutlined style={{ color: '#1890ff', fontSize: 20 }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('edit_supplier')}</span></Space>}
        open={editModalVisible} onCancel={() => { setEditModalVisible(false); editForm.resetFields(); }} footer={null} width={600}>
        <Form form={editForm} layout="vertical" onFinish={handleEditSupplier} style={{ marginTop: 24 }}>
          <Form.Item name="supplierName" label={<strong>{t('supplier_name')}</strong>}
            rules={[{ required: true, message: t('please_enter_supplier_name') }, { min: 2, message: t('min_2_characters') }]}>
            <Input size="large" placeholder={t('enter_supplier_name')} prefix={<IdcardOutlined style={{ color: '#8c8c8c' }} />} />
          </Form.Item>
          <Form.Item name="category" label={<strong>{t('category')}</strong>}>
            <Input size="large" placeholder={t('enter_category_optional')} prefix={<SafetyOutlined style={{ color: '#8c8c8c' }} />} />
          </Form.Item>
          {/* ✅ Email read-only */}
          <Form.Item label={<strong>{t('email')}</strong>}>
            <Input size="large" value={supplierDetails?.businessEmail || ''} disabled
              prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
              style={{ backgroundColor: '#f5f5f5', color: '#595959', cursor: 'not-allowed' }} />
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: 4 }}>{t('email_cannot_be_changed')}</div>
          </Form.Item>
          <Form.Item name="address" label={<strong>{t('address')}</strong>}>
            <TextArea rows={3} placeholder={t('enter_address_optional')} />
          </Form.Item>
          <Alert message={t('note')} description={t('supplier_code_level_cannot_be_changed')} type="warning" showIcon style={{ marginBottom: 16 }} />
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setEditModalVisible(false); editForm.resetFields(); }} size="large">{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />} size="large">{t('save_changes')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Add Certificate ── */}
      <Modal
        title={<Space><PlusOutlined style={{ color: '#52c41a', fontSize: 20 }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('add_certificate')}</span></Space>}
        open={certAddModalVisible} onCancel={() => { setCertAddModalVisible(false); certAddForm.resetFields(); setCertAddFileList([]); }} footer={null} width={560}>
        <Form form={certAddForm} layout="vertical" onFinish={handleCertAdd} style={{ marginTop: 24 }}>
          <Form.Item name="certificateName" label={<strong>{t('certificate_name')}</strong>}
            rules={[{ required: true, message: t('please_select_certificate') }]}>
            <Select size="large" placeholder={t('select_certificate')}>
              {CERTIFICATE_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="validityDate" label={<strong>{t('validity_date')}</strong>}
            rules={[{ required: true, message: t('please_select_validity_date') }]}>
            <DatePicker size="large" style={{ width: '100%' }} format="DD/MM/YYYY"
              disabledDate={(d) => d && d < moment().startOf('day')} />
          </Form.Item>
          <Form.Item label={<strong>{t('upload_document')}</strong>}>
            <Upload fileList={certAddFileList} onChange={({ fileList }) => setCertAddFileList(fileList)}
              beforeUpload={() => false} maxCount={1} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
              <Button icon={<UploadOutlined />}>{t('select_file')}</Button>
            </Upload>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: 4 }}>{t('file_optional')} — PDF, JPG, PNG, DOC, DOCX</div>
          </Form.Item>
          <Alert message={t('admin_certificate_info')} description={t('admin_certificate_auto_approved')} type="success" showIcon style={{ marginBottom: 16 }} />
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCertAddModalVisible(false); certAddForm.resetFields(); setCertAddFileList([]); }} size="large">{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={certSubmitting} icon={<SaveOutlined />} size="large">{t('add_certificate')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Certificate ── */}
      <Modal
        title={<Space><EditOutlined style={{ color: '#1890ff', fontSize: 20 }} /><span style={{ fontSize: '18px', fontWeight: '600' }}>{t('edit_certificate')}</span></Space>}
        open={certEditModalVisible} onCancel={() => { setCertEditModalVisible(false); certEditForm.resetFields(); setCertEditFileList([]); setEditingCert(null); }} footer={null} width={560}>
        <Form form={certEditForm} layout="vertical" onFinish={handleCertEdit} style={{ marginTop: 24 }}>
          <Form.Item name="certificateName" label={<strong>{t('certificate_name')}</strong>}
            rules={[{ required: true, message: t('please_select_certificate') }]}>
            <Select size="large">
              {CERTIFICATE_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="validityDate" label={<strong>{t('validity_date')}</strong>}
            rules={[{ required: true, message: t('please_select_validity_date') }]}>
            <DatePicker size="large" style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="situation" label={<strong>{t('status')}</strong>}
            rules={[{ required: true, message: t('please_select_status') }]}>
            <Select size="large">
              {SITUATION_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}><Tag color={o.color}>{o.label}</Tag></Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={<strong>{t('replace_document')}</strong>}>
            <Upload fileList={certEditFileList} onChange={({ fileList }) => setCertEditFileList(fileList)}
              beforeUpload={() => false} maxCount={1} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
              <Button icon={<UploadOutlined />}>{t('select_new_file')}</Button>
            </Upload>
            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: 4 }}>{t('leave_empty_to_keep_existing_file')}</div>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCertEditModalVisible(false); certEditForm.resetFields(); setCertEditFileList([]); setEditingCert(null); }} size="large">{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={certSubmitting} icon={<SaveOutlined />} size="large">{t('save_changes')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Add / Edit Contact Modal ── */}
      <Modal
        title={
          <Space>
            {editingContact
              ? <EditOutlined style={{ color: '#1890ff', fontSize: 20 }} />
              : <PlusOutlined  style={{ color: '#52c41a', fontSize: 20 }} />}
            <span style={{ fontSize: '18px', fontWeight: '600' }}>
              {editingContact ? t('edit_contact') : t('add_new_contact')}
            </span>
          </Space>
        }
        open={contactModalVisible}
        onCancel={handleContactModalClose}
        footer={null}
        width={600}
      >
        <Form form={contactForm} layout="vertical" onFinish={handleContactSubmit} style={{ marginTop: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label={<strong>{t('full_name')}</strong>}
                rules={[{ required: true, message: t('please_input_name') }]}>
                <Input size="large" prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder={t('enter_full_name')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contactTypeId" label={<strong>{t('contact_type')}</strong>}
                rules={[{ required: true, message: t('please_select_contact_type') }]}>
                <Select size="large" placeholder={t('select_contact_type')}>
                  {contactTypes.map((type) => (
                    <Option key={type.contactTypeId} value={type.contactTypeId}>
                      <Space size={8}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getContactTypeColor(type.name) }} />
                        {type.name}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label={<strong>{t('phone_number')}</strong>}
                rules={[{ required: true, message: t('please_input_phone') }]}>
                <Input size="large" prefix={<PhoneOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder={t('enter_phone_number')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label={<strong>{t('email_address')}</strong>}
                rules={[{ required: true, message: t('please_input_email') }, { type: 'email', message: t('please_enter_valid_email') }]}>
                <Input size="large" prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
                  placeholder={t('enter_email_address')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleContactModalClose} size="large">{t('cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={contactSubmitting}
                icon={editingContact ? <SaveOutlined /> : <PlusOutlined />} size="large">
                {editingContact ? t('save_changes') : t('add_new_contact')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default SupplierPage;
